import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import {
  PdsBox,
  PdsCard,
  PdsMantineText,
  PdsMantineButton,
  PdsLoader,
  PdsChevronDownSmIcon,
  PdsChevronRightSmIcon,
  PdsExternalLinkIcon,
} from '@podium-design-system/react-components';
import CaseCompactActions from './CaseCompactActions';

const STATUS_LABEL = {
  ACTIVE: 'Active',
  PAUSED: 'Paused',
  COMPLETED: 'Completed',
};

/** Pill badge aligned with Argus status visuals (green active, rose paused, slate completed). */
const CASE_STATUS_BADGE_THEME = {
  ACTIVE: { bg: '#ecfdf5', border: '#6ee7b7', color: '#166534' },
  PAUSED: { bg: '#fef2f2', border: '#fecaca', color: '#b91c1c' },
  COMPLETED: { bg: '#f1f5f9', border: '#cbd5e1', color: '#475569' },
  TEMPORARY: { bg: '#fffbeb', border: '#fde68a', color: '#92400e' },
};

function CaseStatusBadge({ status }) {
  const key = typeof status === 'string' ? status.toUpperCase() : '';
  const theme =
    CASE_STATUS_BADGE_THEME[key] || {
      bg: '#f6f8fa',
      border: '#d0d7de',
      color: '#57606a',
    };
  const label = STATUS_LABEL[key] || status || 'Unknown';
  const upper = typeof label === 'string' ? label.toUpperCase() : label;

  return (
    <span
      role="status"
      aria-label={`Case status: ${label}`}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        padding: '4px 10px',
        borderRadius: 999,
        border: `1px solid ${theme.border}`,
        backgroundColor: theme.bg,
        color: theme.color,
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: '0.06em',
        lineHeight: 1,
        flexShrink: 0,
      }}
    >
      {upper}
      <span
        aria-hidden
        style={{
          width: 8,
          height: 8,
          borderRadius: '50%',
          border: `2px solid ${theme.color}`,
          flexShrink: 0,
          opacity: 0.85,
        }}
      />
    </span>
  );
}

/** Highlight all occurrences of needle (case-insensitive) in text with <strong>. */
function HighlightMatch({ text, needle }) {
  if (!needle || !text) return <span>{text || ''}</span>;
  const parts = text.split(new RegExp(`(${needle.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi'));
  return (
    <span>
      {parts.map((part, i) =>
        part.toLowerCase() === needle.toLowerCase()
          ? <strong key={i} style={{ background: '#fef9c3', borderRadius: 2, padding: '0 1px' }}>{part}</strong>
          : part
      )}
    </span>
  );
}

function CaseResult({ c, needle, onNavigate, onRefreshList }) {
  const [expanded, setExpanded] = useState(false);

  const nodes = c.investigations || [];
  const matchedFindings = c.matchedFindings || [];
  const matchedNotes = c.matchedNotes || [];

  const extraCount = (c.investigationCount || 0) - nodes.length;
  const totalFindingsOnMatchedNodes = nodes.reduce((sum, n) => sum + (n.findingsCount || 0), 0);
  const otherFindingsCount = totalFindingsOnMatchedNodes - matchedFindings.length;

  const hasMatches = nodes.length > 0 || matchedFindings.length > 0 || matchedNotes.length > 0;

  return (
    <PdsBox
      direction="column"
      gap="xs"
      style={{ padding: '12px 14px', background: '#f6f8fa', borderRadius: 8, border: '1px solid #e1e4e8' }}
    >
      {/* Case header row */}
      <PdsBox direction="row" alignItems="flex-start" justifyContent="space-between" gap="sm" wrap="wrap">
        <PdsBox direction="column" gap="xs" style={{ flex: 1, minWidth: 0 }}>
          <button
            type="button"
            onClick={() => setExpanded((x) => !x)}
            style={{ display: 'flex', alignItems: 'center', gap: 6, border: 'none', background: 'transparent', padding: 0, cursor: 'pointer', textAlign: 'left' }}
            aria-expanded={expanded}
          >
            <span style={{ flexShrink: 0, marginTop: 1 }}>
              {expanded ? <PdsChevronDownSmIcon /> : <PdsChevronRightSmIcon />}
            </span>
            <PdsMantineText type="textline" fontWeight="strong" component="span" className="argus-type-card-title" style={{ wordBreak: 'break-word' }}>
              <HighlightMatch text={c.title || c.target || 'Untitled case'} needle={needle} />
            </PdsMantineText>
          </button>
          {/* Match reason — visible even when collapsed */}
          {c.matchReason && (
            <PdsMantineText type="textline" color="neutral" component="span" className="argus-type-meta" style={{ paddingLeft: 22 }}>
              {c.matchReason}
            </PdsMantineText>
          )}
        </PdsBox>
        <PdsBox direction="row" alignItems="center" gap="xs" style={{ flexShrink: 0, flexWrap: 'wrap' }}>
          <CaseStatusBadge status={c.status} />
          <PdsMantineButton
            rank="outline"
            color="neutral"
            size="sm"
            onClick={() => onNavigate(c.id, null, null, null)}
            leadingIcon={<PdsExternalLinkIcon size="xs" />}
          >
            Open case
          </PdsMantineButton>
          <CaseCompactActions caseRow={c} onRefreshList={onRefreshList} />
        </PdsBox>
      </PdsBox>

      {/* Expanded content */}
      {expanded && hasMatches && (
        <PdsBox direction="column" gap="sm" style={{ marginTop: 8, paddingTop: 10, borderTop: '1px solid #d0d7de' }}>

          {/* Matched investigation nodes */}
          {nodes.length > 0 && (
            <PdsBox direction="column" gap="xs">
              <PdsMantineText type="textline" fontWeight="strong" component="span" className="argus-type-meta" style={{ color: '#57606a', textTransform: 'uppercase', letterSpacing: '0.04em', fontSize: 11 }}>
                Matched nodes
              </PdsMantineText>
              {nodes.map((inv) => (
                <PdsBox key={inv.id} direction="row" alignItems="center" justifyContent="space-between" gap="sm" wrap="wrap"
                  style={{ paddingLeft: 10, borderLeft: '2px solid #0969da', paddingTop: 4, paddingBottom: 4 }}>
                  <PdsBox direction="column" gap="xs" style={{ flex: 1, minWidth: 0 }}>
                    <PdsBox direction="row" alignItems="center" gap="xs" wrap="wrap">
                      <button
                        type="button"
                        className="cases-investigation-node-link argus-type-meta"
                        onClick={() => onNavigate(c.id, inv.id, null, null)}
                        title={`Open case and focus ${inv.targetValue} on the map`}
                        style={{ textAlign: 'left' }}
                      >
                        <HighlightMatch text={inv.targetValue} needle={needle} />
                      </button>
                      {inv.deriveFrom && (
                        <PdsMantineText type="textline" component="span" className="argus-type-meta" style={{ color: '#57606a' }}>
                          · {String(inv.deriveFrom).replace(/_/g, ' ').replace(/\b\w/g, (ch) => ch.toUpperCase())}
                        </PdsMantineText>
                      )}
                    </PdsBox>
                    {inv.findingsCount > 0 && (
                      <PdsMantineText type="textline" color="neutral" component="span" className="argus-type-meta">
                        {inv.findingsCount} finding{inv.findingsCount !== 1 ? 's' : ''} on this node
                      </PdsMantineText>
                    )}
                  </PdsBox>
                  <PdsBox direction="row" alignItems="center" gap="xs" wrap="wrap" style={{ flexShrink: 0 }}>
                    <PdsMantineButton
                      rank="outline"
                      color="action"
                      size="sm"
                      onClick={() => {
                        const nodeFinding = matchedFindings.find(
                          (f) => f.investigationId === inv.id && f.sourceKind && f.sourceKind !== 'OTHER_INFORMATION',
                        );
                        onNavigate(
                          c.id,
                          inv.id,
                          null,
                          null,
                          nodeFinding ? { sourceKind: nodeFinding.sourceKind, needle } : {},
                        );
                      }}
                    >
                      View node →
                    </PdsMantineButton>
                    <CaseCompactActions caseRow={c} onRefreshList={onRefreshList} />
                  </PdsBox>
                </PdsBox>
              ))}
              {extraCount > 0 && (
                <PdsMantineText type="textline" color="neutral" component="span" className="argus-type-meta" style={{ paddingLeft: 12 }}>
                  +{extraCount} other node{extraCount !== 1 ? 's' : ''} — open case to see full map
                </PdsMantineText>
              )}
            </PdsBox>
          )}

          {/* Matched findings */}
          {matchedFindings.length > 0 && (
            <PdsBox direction="column" gap="xs">
              <PdsMantineText type="textline" fontWeight="strong" component="span" className="argus-type-meta" style={{ color: '#57606a', textTransform: 'uppercase', letterSpacing: '0.04em', fontSize: 11 }}>
                Matched findings
              </PdsMantineText>
              {matchedFindings.map((f, i) => (
                <PdsBox key={f.id || i} direction="row" alignItems="flex-start" justifyContent="space-between" gap="sm" wrap="wrap"
                  style={{ paddingLeft: 10, borderLeft: '2px solid #22c55e', paddingTop: 4, paddingBottom: 4 }}>
                  <PdsBox direction="column" gap="xs" style={{ flex: 1, minWidth: 0 }}>
                    <PdsMantineText type="textline" component="span" className="argus-type-meta">
                      <span style={{ color: '#57606a' }}>{f.categoryLabel || f.informationType}: </span>
                      <HighlightMatch text={f.informationValue} needle={needle} />
                    </PdsMantineText>
                    {f.remarks && (
                      <PdsMantineText type="textline" color="neutral" component="span" className="argus-type-meta">
                        <HighlightMatch text={f.remarks} needle={needle} />
                      </PdsMantineText>
                    )}
                  </PdsBox>
                  {((!f.sourceKind || f.sourceKind === 'OTHER_INFORMATION') ? f.id != null : f.investigationId) && (
                    <PdsMantineButton
                      rank="outline"
                      color="action"
                      size="sm"
                      onClick={() => onNavigate(
                        c.id,
                        f.investigationId || null,
                        (!f.sourceKind || f.sourceKind === 'OTHER_INFORMATION') ? f.id : null,
                        null,
                        (!f.sourceKind || f.sourceKind === 'OTHER_INFORMATION')
                          ? {}
                          : { sourceKind: f.sourceKind, needle },
                      )}
                    >
                      {(!f.sourceKind || f.sourceKind === 'OTHER_INFORMATION') ? 'Go to finding →' : 'View node →'}
                    </PdsMantineButton>
                  )}
                </PdsBox>
              ))}
              {otherFindingsCount > 0 && (
                <PdsMantineText type="textline" color="neutral" component="span" className="argus-type-meta" style={{ paddingLeft: 12 }}>
                  +{otherFindingsCount} other finding{otherFindingsCount !== 1 ? 's' : ''} on matched nodes
                </PdsMantineText>
              )}
            </PdsBox>
          )}

          {/* Matched notes */}
          {matchedNotes.length > 0 && (
            <PdsBox direction="column" gap="xs">
              <PdsMantineText type="textline" fontWeight="strong" component="span" className="argus-type-meta" style={{ color: '#57606a', textTransform: 'uppercase', letterSpacing: '0.04em', fontSize: 11 }}>
                Matched case notes
              </PdsMantineText>
              {matchedNotes.map((n, i) => (
                <PdsBox key={n.id || i} direction="row" alignItems="flex-start" justifyContent="space-between" gap="sm" wrap="wrap"
                  style={{ paddingLeft: 10, borderLeft: '2px solid #f59e0b', paddingTop: 4, paddingBottom: 4 }}>
                  <PdsBox direction="column" gap="xs" style={{ flex: 1, minWidth: 0 }}>
                    <PdsMantineText type="textline" component="span" className="argus-type-meta" style={{ wordBreak: 'break-word' }}>
                      <HighlightMatch text={n.excerpt} needle={needle} />
                    </PdsMantineText>
                    {n.authorUserId && (
                      <PdsMantineText type="textline" color="neutral" component="span" className="argus-type-meta">
                        {n.authorUserId.includes('@') ? n.authorUserId.split('@')[0] : n.authorUserId}
                        {n.createdAt && ` · ${new Date(n.createdAt).toLocaleDateString()}`}
                      </PdsMantineText>
                    )}
                  </PdsBox>
                  {n.id && (
                    <PdsMantineButton
                      rank="outline"
                      color="action"
                      size="sm"
                      onClick={() => onNavigate(c.id, null, null, n.id)}
                    >
                      Go to note →
                    </PdsMantineButton>
                  )}
                </PdsBox>
              ))}
            </PdsBox>
          )}

          {/* See full case CTA */}
          <PdsBox direction="row" justifyContent="flex-end" style={{ marginTop: 4 }}>
            <PdsMantineButton
              rank="outline"
              color="neutral"
              size="sm"
              onClick={() => onNavigate(c.id, null, null, null)}
            >
              See full case
            </PdsMantineButton>
          </PdsBox>
        </PdsBox>
      )}

      {expanded && !hasMatches && (
        <PdsBox direction="column" gap="xs" style={{ paddingLeft: 22, marginTop: 4 }}>
          {c.description ? (
            <PdsMantineText type="textline" color="neutral" component="span" className="argus-type-meta" style={{ wordBreak: 'break-word' }}>
              <HighlightMatch text={c.description} needle={needle} />
            </PdsMantineText>
          ) : (
            <PdsMantineText type="textline" color="neutral" component="span" className="argus-type-meta">
              Open the case to explore details.
            </PdsMantineText>
          )}
        </PdsBox>
      )}
    </PdsBox>
  );
}

/**
 * Home search companion: lists saved cases that reference the query (live debounced).
 * Shows collapsed match-reason line; expands to reveal matched nodes, findings, and notes with CTAs.
 */
export default function HomeExistingCasesPanel({ displayTerm, searchPayload }) {
  const navigate = useNavigate();
  const [panelOpen, setPanelOpen] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [refreshNonce, setRefreshNonce] = useState(0);

  const bumpRefresh = useCallback(() => setRefreshNonce((n) => n + 1), []);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (!searchPayload) {
        setData(null);
        setError(null);
        return;
      }
      setLoading(true);
      setError(null);
      try {
        const res = await axios.post('/api/cases/search', searchPayload);
        if (!cancelled) setData(res.data);
      } catch (e) {
        if (!cancelled) {
          setError(e.response?.data?.message || e.message || 'Search failed.');
          setData(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    return () => { cancelled = true; };
  }, [searchPayload, refreshNonce]);

  if (!displayTerm?.trim()) return null;

  const label = displayTerm.trim();
  const total = data?.totalElements ?? 0;
  const results = data?.results || [];

  const summaryLine = loading
    ? null
    : error
      ? error
      : total === 0
        ? `No mentions of "${label}" in existing cases.`
        : `${total} existing case${total !== 1 ? 's' : ''} mention "${label}".`;

  const handleNavigate = (caseId, investigationId, findingId, noteId, extra = {}) => {
    const state = {};
    if (investigationId) state.selectedInvestigationId = investigationId;
    if (findingId) state.jumpToFindingId = String(findingId);
    if (noteId) { state.caseSubPage = 'notes'; state.jumpToNoteId = String(noteId); }
    if (extra.sourceKind) state.jumpToSourceKind = extra.sourceKind;
    if (extra.needle) state.jumpToNeedle = extra.needle;
    navigate(`/cases/${caseId}`, { state });
  };

  return (
    <>
      <PdsCard
      border
      borderRadius="md"
      padding="md"
      color="neutral"
      contrast="min"
      style={{ width: '100%' }}
    >
      {/* Panel header / toggle */}
      <button
        type="button"
        onClick={() => setPanelOpen((x) => !x)}
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 10,
          width: '100%',
          border: 'none',
          background: 'transparent',
          padding: 0,
          cursor: 'pointer',
          textAlign: 'left',
        }}
        aria-expanded={panelOpen}
      >
        <span style={{ marginTop: 2, flexShrink: 0 }}>
          {panelOpen ? <PdsChevronDownSmIcon /> : <PdsChevronRightSmIcon />}
        </span>
        <PdsBox direction="column" gap="xs" style={{ flex: 1 }}>
          <PdsMantineText type="textline" fontWeight="strong" component="span" className="argus-type-card-title">
            Existing cases on Argus
          </PdsMantineText>
          <PdsMantineText type="textline" color="neutral" component="span">
            {loading ? (
              <PdsBox direction="row" alignItems="center" gap="sm">
                <PdsLoader size="sm" />
                Searching saved cases…
              </PdsBox>
            ) : (
              summaryLine
            )}
          </PdsMantineText>
        </PdsBox>
      </button>

      {panelOpen && !loading && results.length > 0 && (
        <PdsBox direction="column" gap="sm" style={{ marginTop: 14, paddingTop: 14, borderTop: '1px solid #eaeef2' }}>
          {results.map((c) => (
            <CaseResult key={c.id} c={c} needle={label} onNavigate={handleNavigate} onRefreshList={bumpRefresh} />
          ))}
        </PdsBox>
      )}

      {panelOpen && !loading && !error && total > 80 && (
        <PdsMantineText type="textline" color="neutral" component="p" style={{ marginTop: 12 }} className="argus-type-meta">
          Showing first 80 cases. Narrow your query or use Cases search for filters.
        </PdsMantineText>
      )}
    </PdsCard>
    </>
  );
}
